import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, Plus, History, Mic, Square, ChevronDown, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useModuleAccess } from '../../contexts/ModuleAccessContext';
import { panelContext, grupoDoBloco, TELA_LABEL } from './assistantContext';
import { catalogo, montar, rotuloDe, PERIODOS, type Pergunta } from './perguntas';
import { usePickerData } from './usePickerData';
import { assistantService, type ChatItem } from './assistantService';
import { useSpeechInput } from './useSpeechInput';

const fmtHoras = (h: number) => {
  const min = Math.round(h * 60);
  const m = min % 60;
  return m ? `${Math.floor(min / 60)}h${String(m).padStart(2, '0')}` : `${Math.floor(min / 60)}h`;
};
const fmtData = (d: string) => d.split('-').reverse().join('/');

const BTN = 'block w-full text-left text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50';

interface Props { section: string }

const NankinAIChat: React.FC<Props> = ({ section }) => {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const { isAdmin, canAccess } = useModuleAccess();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ChatItem[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [tela, setTela] = useState<'time' | 'reports' | 'activities-strategic' | 'price-simulator' | null>(null);
  const [error, setError] = useState<{ message: string; pergunta: string; codigo?: string } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<{ id: string; titulo: string }[]>([]);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Muda a cada reset; respostas em andamento de uma geração antiga são ignoradas.
  const generation = useRef(0);
  const confirmando = useRef<object | null>(null);
  const [gruposAbertos, setGruposAbertos] = useState<Record<string, boolean>>({});
  const [picker, setPicker] = useState<Pergunta | null>(null);
  const [filtro, setFiltro] = useState('');
  const dadosPicker = usePickerData(organizationId, picker?.parametro === 'projeto' || picker?.parametro === 'pessoa');
  const voz = useSpeechInput();
  const pararVoz = useRef(voz.parar);
  pararVoz.current = voz.parar;

  useEffect(() => {
    const toggle = () => setOpen((o) => !o);
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); toggle(); }
    };
    window.addEventListener('nankin-ai:toggle', toggle);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('nankin-ai:toggle', toggle); window.removeEventListener('keydown', onKey); };
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [items, status]);

  const send = useCallback(async (pergunta: string) => {
    const text = pergunta.trim();
    if (!text || busy || !organizationId) return;
    setInput(''); setError(null); setTela(null); setBusy(true);
    const gen = generation.current;
    setItems((prev) => [...prev, { role: 'user', text }, { role: 'assistant', text: '' }]);
    const patchLast = (patch: Partial<ChatItem>) => setItems((prev) => {
      if (prev[prev.length - 1]?.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], ...patch };
      return next;
    });
    const appendText = (t: string) => setItems((prev) => {
      if (prev[prev.length - 1]?.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = { ...next[next.length - 1], text: next[next.length - 1].text + t };
      return next;
    });
    try {
      await assistantService.ask({ organizationId, conversationId, mensagem: text, section }, (e) => {
        if (generation.current !== gen) return;
        if (e.type === 'conversa') setConversationId(e.conversation_id);
        else if (e.type === 'texto') { setStatus(null); appendText(e.texto); }
        else if (e.type === 'status') setStatus(e.ferramenta);
        else if (e.type === 'tela') setTela(e.tela);
        else if (e.type === 'proposta') patchLast({ proposta: e.lancamentos, propostaEstado: 'pendente', propostaMensagem: undefined });
        else if (e.type === 'simulacao') patchLast({ simulacao: { nome: e.nome, por_cargo: e.por_cargo } });
        else if (e.type === 'erro') throw new Error(e.erro);
      });
    } catch (err) {
      if (generation.current !== gen) return;
      // Tira a pergunta e a resposta incompleta; o texto volta no "Tentar de novo".
      setItems((prev) => prev.slice(0, -2));
      const codigo = (err as { codigo?: string } | null)?.codigo;
      setError({ message: err instanceof Error ? err.message : 'Não consegui responder agora.', pergunta: text, codigo });
    } finally {
      if (generation.current === gen) { setBusy(false); setStatus(null); }
    }
  }, [busy, organizationId, conversationId, section]);

  const confirmar = async (idx: number) => {
    const item = items[idx];
    if (!organizationId || !item?.proposta || confirmando.current) return;
    const token = {};
    confirmando.current = token;
    const gen = generation.current;
    const patch = (p: Partial<ChatItem>) => setItems((prev) => prev.map((m, n) => (n === idx ? { ...m, ...p } : m)));
    patch({ propostaEstado: 'enviando', propostaMensagem: undefined });
    try {
      const r = await assistantService.confirmarLancamentos(organizationId, item.proposta);
      // As horas já foram gravadas: avisa as telas mesmo que a conversa tenha mudado.
      window.dispatchEvent(new Event('timeEntriesUpdated'));
      if (generation.current !== gen) return;
      patch({ propostaEstado: 'registrado', propostaMensagem: `Registrado: ${fmtHoras(r.total_horas)} em ${r.criados} lançamento(s).` });
    } catch (err) {
      if (generation.current !== gen) return;
      patch({ propostaEstado: 'erro', propostaMensagem: err instanceof Error ? err.message : 'Não consegui registrar os lançamentos.' });
    } finally {
      if (confirmando.current === token) confirmando.current = null;
    }
  };

  const abrirSimulador = (s: NonNullable<ChatItem['simulacao']>) => {
    try { sessionStorage.setItem('nankin-ai:simulacao', JSON.stringify({ nome: s.nome, por_cargo: s.por_cargo })); } catch { /* sem sessionStorage */ }
    // Se o Simulador já está aberto, ele só ouve o evento; a navegação não remonta a tela.
    window.dispatchEvent(new Event('nankin-ai:simulacao'));
    navigate('/price-simulator');
  };

  const preencher = (texto: string) => {
    setInput(texto);
    inputRef.current?.focus();
  };

  const reset = useCallback(() => {
    generation.current += 1;
    confirmando.current = null;
    pararVoz.current();
    setBusy(false); setStatus(null);
    setItems([]); setConversationId(undefined); setError(null); setTela(null); setShowHistory(false); setHistory([]);
    setPicker(null); setFiltro('');
  }, []);
  const newConversation = reset;

  // Trocar de organização recomeça: a conversa antiga não pertence à nova.
  useEffect(() => { reset(); }, [organizationId, reset]);

  const openHistory = async () => {
    if (!organizationId) return;
    setShowHistory((s) => !s);
    try { setHistory(await assistantService.listConversations(organizationId)); } catch { setHistory([]); }
  };

  const loadConversation = async (id: string) => {
    // Abrir outra conversa invalida a resposta que ainda chega da anterior.
    const gen = ++generation.current;
    setBusy(false); setStatus(null);
    setShowHistory(false); setError(null); setTela(null);
    try {
      const loaded = await assistantService.loadMessages(id);
      if (generation.current !== gen) return;
      setItems(loaded); setConversationId(id);
    } catch {
      if (generation.current !== gen) return;
      setError({ message: 'Não consegui abrir a conversa.', pergunta: '' });
    }
  };

  if (!open) return null;

  const ctx = panelContext(section, isAdmin);
  const semAcesso = !(['time', 'projects', 'activities-strategic', 'price-simulator'] as const).some((m) => canAccess(m));
  const grupoPadrao = grupoDoBloco(section);
  const grupos = catalogo({ isAdmin, canAccess })
    .map((g) => ({ ...g, perguntas: g.perguntas.filter((p) => !ctx.sugestoes.includes(p.texto)) }))
    .filter((g) => g.perguntas.length > 0);
  const escolher = (p: Pergunta) => {
    if (p.parametro) { setFiltro(''); setPicker((cur) => (cur?.id === p.id ? null : p)); return; }
    setPicker(null);
    if (p.acao === 'preencher') preencher(p.texto); else send(p.texto);
  };
  const responder = (p: Pergunta, valor: string) => { setPicker(null); setFiltro(''); send(montar(p, valor)); };
  const opcoes = (picker?.parametro === 'pessoa' ? dadosPicker.pessoas : dadosPicker.projetos)
    .filter((n) => n.toLowerCase().includes(filtro.trim().toLowerCase()));
  const pensando = busy && !status && items[items.length - 1]?.role === 'assistant' && !items[items.length - 1].text;
  const semRetentativa = ['limite', 'conversa_longa', 'sem_acesso'].includes(error?.codigo ?? '');

  return (
    <aside className="fixed inset-0 md:inset-auto md:top-0 md:right-0 md:h-screen md:w-[400px] z-40 bg-white border-l border-gray-200 shadow-xl flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2 font-semibold text-gray-900">
          <Sparkles className="w-5 h-5 text-coral" /> {ctx.titulo}
        </div>
        <div className="flex items-center gap-1">
          <button aria-label="Histórico" title="Conversas anteriores" onClick={openHistory} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-gray-100"><History className="w-4 h-4" /><span aria-hidden="true">Histórico</span></button>
          <button aria-label="Nova conversa" title="Começar uma conversa nova" onClick={newConversation} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-gray-100"><Plus className="w-4 h-4" /><span aria-hidden="true">Nova</span></button>
          <button aria-label="Fechar" title="Fechar o assistente" onClick={() => { voz.parar(); setOpen(false); }} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-gray-100"><X className="w-4 h-4" /><span aria-hidden="true">Fechar</span></button>
        </div>
      </header>

      {showHistory && (
        <ul className="border-b border-gray-200 max-h-60 overflow-y-auto">
          {history.length === 0 && <li className="px-4 py-3 text-sm text-gray-500">Nenhuma conversa ainda.</li>}
          {history.map((h) => (
            <li key={h.id}>
              <button onClick={() => loadConversation(h.id)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 truncate">{h.titulo || 'Sem título'}</button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {semAcesso && <p className="text-sm text-gray-600">Sua função não tem acesso ao assistente.</p>}
        {!semAcesso && items.length === 0 && !error && (
          <div className="space-y-2">
            {ctx.sugestoes.map((s) => (
              <button
                key={s} className={BTN}
                onClick={() => (s.endsWith('…') ? preencher(`${s.slice(0, -1).trimEnd()} `) : send(s))}
              >{s}</button>
            ))}
            {grupos.length > 0 && (
              <section aria-label="Perguntas prontas" className="pt-2 space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">Perguntas prontas</h3>
                {grupos.map((g) => {
                  const aberto = gruposAbertos[g.grupo] ?? g.grupo === grupoPadrao;
                  return (
                    <div key={g.grupo}>
                      <button
                        type="button" aria-expanded={aberto}
                        onClick={() => {
                          setGruposAbertos((cur) => ({ ...cur, [g.grupo]: !aberto }));
                          if (aberto && g.perguntas.some((x) => x.id === picker?.id)) setPicker(null);
                        }}
                        className="flex w-full items-center gap-1 py-1 text-sm text-gray-700 hover:text-gray-900"
                      >
                        {aberto ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        {g.grupo}
                      </button>
                      {aberto && (
                        <div className="space-y-1 pb-1">
                          {g.perguntas.map((p) => (
                            <div key={p.id}>
                              <button className={BTN} aria-expanded={p.parametro ? picker?.id === p.id : undefined} onClick={() => escolher(p)}>{rotuloDe(p)}</button>
                              {picker?.id === p.id && (
                                <div className="mt-1 rounded-lg border border-gray-200 p-2 space-y-2 text-sm">
                                  {p.parametro === 'periodo' ? (
                                    <div className="flex flex-wrap gap-1">
                                      {PERIODOS.map((per) => (
                                        <button type="button" key={per.rotulo} onClick={() => responder(p, per.texto)} className="px-2 py-1 rounded-full border border-gray-300 hover:bg-gray-50">{per.rotulo}</button>
                                      ))}
                                    </div>
                                  ) : dadosPicker.loading ? (
                                    <p className="text-gray-500">Carregando…</p>
                                  ) : dadosPicker.error ? (
                                    <p role="alert" className="text-red-700">Não consegui carregar a lista.</p>
                                  ) : (
                                    <>
                                      <input
                                        value={filtro} onChange={(e) => setFiltro(e.target.value)} autoFocus
                                        aria-label={p.parametro === 'pessoa' ? 'Filtrar pessoas' : 'Filtrar projetos'}
                                        placeholder={p.parametro === 'pessoa' ? 'Filtrar pessoas…' : 'Filtrar projetos…'}
                                        className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
                                      />
                                      <ul className="max-h-48 overflow-y-auto">
                                        {opcoes.length === 0 && <li className="px-2 py-1 text-gray-500">Nada encontrado.</li>}
                                        {opcoes.map((n, i) => (
                                          <li key={`${i}-${n}`}><button type="button" onClick={() => responder(p, n)} className="w-full text-left px-2 py-1 rounded hover:bg-gray-50">{n}</button></li>
                                        ))}
                                      </ul>
                                    </>
                                  )}
                                  <button type="button" onClick={() => setPicker(null)} className="text-gray-600 underline">Cancelar</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            )}
          </div>
        )}
        {items.map((m, i) => (
          <div
            key={i}
            className={m.role === 'user'
              ? 'ml-8 rounded-lg bg-gray-100 px-3 py-2 text-sm'
              : 'text-sm text-gray-800 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_table]:text-xs [&_td]:px-2 [&_th]:px-2'}
          >
            {m.role === 'user' ? m.text : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>}
            {m.proposta && m.propostaEstado && (
              <div className="mt-2 rounded-lg border border-gray-200 p-3 space-y-2 text-gray-800">
                {m.propostaEstado === 'registrado' || m.propostaEstado === 'cancelado' ? (
                  <p role="status">{m.propostaEstado === 'cancelado' ? 'Cancelado.' : m.propostaMensagem}</p>
                ) : (
                  <>
                    <ul className="space-y-1">
                      {m.proposta.map((l, n) => (
                        <li key={n}>
                          {fmtData(l.data)} · {l.inicio}–{l.fim} · {l.projeto} · {l.descricao} · {fmtHoras(l.horas)}
                          {l.sobreposicao && <span className="block text-xs text-amber-700">Sobrepõe um horário já lançado.</span>}
                        </li>
                      ))}
                    </ul>
                    {m.propostaEstado === 'erro' && <p role="alert" className="text-red-700">{m.propostaMensagem}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => confirmar(i)} disabled={m.propostaEstado === 'enviando'} className="btn-primary-sm">Confirmar</button>
                      <button
                        onClick={() => setItems((prev) => prev.map((x, n) => (n === i ? { ...x, propostaEstado: 'cancelado' } : x)))}
                        disabled={m.propostaEstado === 'enviando'} className="btn-outline-sm"
                      >Cancelar</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {m.simulacao && (
              <button onClick={() => abrirSimulador(m.simulacao!)} className="mt-2 text-coral hover:underline">Abrir no Simulador</button>
            )}
          </div>
        ))}
        {(status || pensando) && <p className="text-xs text-gray-500">{status ?? 'Pensando…'}</p>}
        {tela && !busy && (
          <button onClick={() => navigate(`/${tela}`)} className="text-sm text-coral hover:underline">Abrir em {TELA_LABEL[tela]}</button>
        )}
        {voz.erro && <p role="alert" className="text-sm text-red-700">{voz.erro}</p>}
        {error && (
          <div className="text-sm text-red-700 space-y-2">
            <p>{error.message}</p>
            {error.pergunta && !semRetentativa && <button onClick={() => send(error.pergunta)} className="underline">Tentar de novo</button>}
            {error.codigo === 'conversa_longa' && <button onClick={newConversation} className="underline">Nova conversa</button>}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {!semAcesso && (
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-gray-200 p-3 flex gap-2">
          <input
            ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} maxLength={2000} disabled={busy}
            placeholder="Pergunte sobre suas horas…"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral"
          />
          {voz.suportado && (
            <button
              type="button" disabled={busy} aria-label={voz.ouvindo ? 'Parar' : 'Falar'} aria-pressed={voz.ouvindo}
              onClick={() => (voz.ouvindo ? voz.parar() : voz.iniciar((t) => setInput((cur) => (cur ? `${cur} ${t}` : t))))}
              className="p-2 rounded-lg border border-gray-300 disabled:opacity-50"
            >
              {voz.ouvindo ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button type="submit" disabled={busy || !input.trim()} aria-label="Enviar" className="p-2 rounded-lg bg-coral-deep text-white disabled:opacity-50">
            <Send className="w-4 h-4" />
          </button>
        </form>
      )}
    </aside>
  );
};

export default NankinAIChat;
